import { useGetEmployeesQuery } from "../redux/services/employees/employeesApi";
import { useGetOrganizationsQuery } from "../redux/services/organizations/organizationsApi";
import { CheckboxList } from "./CheckboxList";
import { useState, useMemo, useEffect } from "react";
import { useDebounceSearch } from "../hooks";
import { Button, Input } from "../shared";

/**
 * EmployeesFilterList component for filtering payouts by employees and organizations
 * Organizations are prefixed with "org_" to identify them
 * Selecting an organization will filter for all drivers/employees in that organization
 */
export const EmployeesFilterList = (props) => {
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const { search, handleSearch: handleDebounceSearch } = useDebounceSearch();

  // Fetch employees - increase limit when searching to get more results for client-side filtering
  const { data: employeesData, isLoading: isLoadingEmployees } =
    useGetEmployeesQuery({
      page: pageIndex + 1,
      limit: search.useSearch && search.term ? 100 : pageSize, // Fetch more when searching
      search: search.useSearch,
      searchTerm: search.term,
    });

  // Fetch organizations
  const { data: organizationsData, isLoading: isLoadingOrganizations } =
    useGetOrganizationsQuery({});

  const isLoading = isLoadingEmployees || isLoadingOrganizations;

  // Combine employees and organizations for display
  const combinedData = useMemo(() => {
    const employees =
      employeesData?.employees?.map((employee) => ({
        _id: employee._id,
        name: employee.fullName || employee.firstName || employee.lastName || "No Name",
        type: "employee",
        // Include additional fields for better search matching
        email: employee.email || "",
        firstName: employee.firstName || "",
        lastName: employee.lastName || "",
      })) || [];

    const organizations =
      organizationsData?.map((org) => ({
        _id: `org_${org._id}`, // Prefix to identify as organization
        name: `${org.name || "Unnamed Organization"} (ORG)`,
        type: "organization",
        organizationId: org._id,
      })) || [];

    // Filter by search term if searching
    const allItems = [...organizations, ...employees];
    if (search.term && search.useSearch && search.term.trim()) {
      const searchLower = search.term.toLowerCase().trim();
      return allItems.filter((item) => {
        // Search in name
        if (item.name.toLowerCase().includes(searchLower)) {
          return true;
        }
        // Also search in email for employees
        if (item.type === "employee" && item.email) {
          return item.email.toLowerCase().includes(searchLower);
        }
        return false;
      });
    }

    return allItems;
  }, [employeesData, organizationsData, search.term, search.useSearch]);

  function handlePageChange(newPage) {
    setPagination((prev) => ({
      ...prev,
      pageIndex: newPage,
    }));
  }

  function handleSearch(value) {
    setPagination({ pageIndex: 0, pageSize });
    handleDebounceSearch(value);
  }

  // Reset pagination when search term changes
  useEffect(() => {
    if (search.term !== undefined) {
      setPagination({ pageIndex: 0, pageSize });
    }
  }, [search.term]);

  function handleChange(val) {
    const title =
      val.map(({ name }) => name).join(", ") || props.value?.defaultText;
    let value = val.map((item) => item._id);

    if (Array.isArray(value)) {
      if (value.length === 0) {
        value = null;
      } else {
        value = value.join(",");
      }
    }

    props.onChange({ title, value });
  }

  return (
    <div style={{ minWidth: "300px" }}>
      <Input
        type="text"
        placeholder="Search employees or organizations..."
        onChange={(e) => handleSearch(e.value)}
      />
      <CheckboxList
        objectKey="_id"
        value={props.value.value}
        onChange={handleChange}
        data={combinedData}
        loading={isLoading}
      />
      <div className="flex items-center justify-between mt-2">
        <Button
          onClick={() => handlePageChange(pageIndex - 1)}
          disabled={pageIndex === 0}
          type="light"
          size="xs"
        >
          Previous
        </Button>
        <span className="text-xs">Page {pageIndex + 1}</span>
        <Button
          onClick={() => handlePageChange(pageIndex + 1)}
          disabled={
            !employeesData?.employees?.length ||
            employeesData?.employees?.length < pageSize ||
            (search.useSearch && search.term) // Disable pagination when searching
          }
          type="light"
          size="xs"
        >
          Next
        </Button>
      </div>
    </div>
  );
};
